import type {
  PrizeHistoryItem,
  RoulettePopup,
  RoulettePrize,
} from '~/types/roulette';
import { resolveCloudflareImage } from '~/utils/apostatudo/cloudflare-image';

// Mapeadores snake_case (core) → camelCase (domínio). Compartilhados entre o
// adapter de detalhe e o de giro (ambos retornam prêmios/popups).

export function mapPopup(raw: any): RoulettePopup | null {
  if (!raw) return null;

  return {
    title: raw.title ?? '',
    imageUrl: resolveCloudflareImage(raw.image_url),
    description: raw.description ?? '',
    // Ausente (back antigo) = false, preservando os CTAs como vinham antes.
    closeOnly: raw.close_only ?? false,
    // Ação só existe com texto: o core manda o objeto com campos null quando o
    // botão não foi configurado (senão renderiza botão vazio no popup).
    primaryAction: raw.primary_action?.text
      ? {
          text: raw.primary_action.text,
          link: raw.primary_action.link ?? '',
          color: raw.primary_action.color ?? undefined,
        }
      : null,
    secondaryAction: raw.secondary_action?.text
      ? {
          text: raw.secondary_action.text,
          link: raw.secondary_action.link ?? '',
          color: raw.secondary_action.color ?? undefined,
        }
      : null,
  };
}

export function mapPrize(raw: any): RoulettePrize {
  return {
    id: raw.id,
    position: raw.position ?? 0,
    prizeName: raw.prize_name ?? null,
    text1: raw.text1 ?? '',
    text2: raw.text2 ?? '',
    sliceImageUrl: resolveCloudflareImage(raw.slice_image_url),
    prizeImageUrl: resolveCloudflareImage(raw.prize_image_url),
    isLoss: raw.is_loss ?? false,
    // numeric(5,2) do Laravel pode serializar como string ("49.50").
    displayProbability:
      raw.display_probability == null ||
      !Number.isFinite(Number(raw.display_probability))
        ? null
        : Number(raw.display_probability),
    colors: {
      classification: raw.colors?.classification ?? '',
      titleBg: raw.colors?.title_bg ?? '',
      sliceBg: raw.colors?.slice_bg ?? '',
    },
    layout: {
      textScale: raw.layout?.text_scale ?? 100,
      text2Scale: raw.layout?.text2_scale ?? 100,
      textOffsetX: raw.layout?.text_offset_x ?? 0,
      textOffsetY: raw.layout?.text_offset_y ?? 0,
      imageScale: raw.layout?.image_scale ?? 100,
      imageOffsetX: raw.layout?.image_offset_x ?? 0,
      imageOffsetY: raw.layout?.image_offset_y ?? 0,
    },
  };
}

// Imagem que representa um prêmio (popup, grid de prêmios, histórico): arte do
// prêmio quando cadastrada; senão a arte da fatia. Cadeia única — quem exibe
// prêmio consome daqui.
export function prizeDisplayImage(
  prize?: {
    prizeImageUrl?: string | null;
    sliceImageUrl?: string | null;
  } | null,
): string {
  return prize?.prizeImageUrl || prize?.sliceImageUrl || '';
}

export function mapHistoryItem(raw: any): PrizeHistoryItem {
  // Back antigo não manda result: infere pela presença do prize.
  const result = raw.result ?? (raw.prize ? 'win' : 'lose');

  return {
    spinId: raw.spin_id,
    result,
    roulette: raw.roulette
      ? { slug: raw.roulette.slug ?? '', title: raw.roulette.title ?? '' }
      : null,
    prize: raw.prize
      ? {
          id: raw.prize.id,
          prizeName: raw.prize.prize_name ?? '',
          prizeImageUrl:
            resolveCloudflareImage(raw.prize.prize_image_url) || null,
          sliceImageUrl:
            resolveCloudflareImage(raw.prize.slice_image_url) || null,
        }
      : null,
    // Entrega só faz sentido em vitória — o "delivered" da derrota é convenção
    // do back e é absorvido aqui, não nos componentes.
    deliveryStatus: result === 'win' ? (raw.delivery_status ?? null) : null,
    spunAt: raw.spun_at ?? null,
  };
}
